import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { WebBaseControlTypedSettingsComponent } from '@web/widgets/common/abstract/web-base-control-typed-settings.component';
import { type ESize, InputBorderEnum } from '@common/enums';
import { ThemeStateService } from '@sites/site-theme/src/state';
import type { SortingSettingsFormGroup, SortingSettingsFormType } from '@web/widgets/catalog-products/type';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { CSS_RESTRICTIONS } from '@common/constants';
import { CommonModule } from '@angular/common';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

@Component({
    selector: 'web-sorting-settings',
    templateUrl: './sorting-settings.component.html',
    styleUrls: ['./sorting-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, CommonElementsModule],
})
export class SortingSettingsComponent extends WebBaseControlTypedSettingsComponent<SortingSettingsFormGroup> {
    private readonly themeState = inject(ThemeStateService);

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public setEnabledButton(value: boolean) {
        this.enable.patchValue(value);
    }

    public setEnabledSearch(value: boolean) {
        this.searchEnable.patchValue(value);
    }

    public setBorderStyle(value: string) {
        this.borderStyle.patchValue(value as InputBorderEnum);
    }

    public get enable(): SortingSettingsFormType['enable'] {
        return this.itemForm.get('enable') as SortingSettingsFormType['enable'];
    }

    public get borderStyle(): SortingSettingsFormType['borderStyle'] {
        return this.itemForm.get('borderStyle') as SortingSettingsFormType['borderStyle'];
    }

    public get textColor(): SortingSettingsFormType['textColor'] {
        return this.itemForm.get('textColor') as SortingSettingsFormType['textColor'];
    }

    public get backgroundColor(): SortingSettingsFormType['backgroundColor'] {
        return this.itemForm.get('backgroundColor') as SortingSettingsFormType['backgroundColor'];
    }

    public get borderColor(): SortingSettingsFormType['borderColor'] {
        return this.itemForm.get('borderColor') as SortingSettingsFormType['borderColor'];
    }

    public get borderRadius(): SortingSettingsFormType['borderRadius'] {
        return this.itemForm.get('borderRadius') as SortingSettingsFormType['borderRadius'];
    }

    public get inputSize(): SortingSettingsFormType['inputSize'] {
        return this.itemForm.get('inputSize') as SortingSettingsFormType['inputSize'];
    }

    public get searchEnable(): SortingSettingsFormType['searchEnable'] {
        return this.itemForm.get('searchEnable') as SortingSettingsFormType['searchEnable'];
    }

    public get searchPlaceholder(): SortingSettingsFormType['searchPlaceholder'] {
        return this.itemForm.get('searchPlaceholder') as SortingSettingsFormType['searchPlaceholder'];
    }

    public setTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.itemForm.markAsTouched({ onlySelf: true });
        this.textColor.patchValue(presetName ?? value);
    }

    public setBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.itemForm.markAsTouched({ onlySelf: true });
        this.backgroundColor.patchValue(presetName ?? value);
    }

    public setBorderColor({ value, presetName }: ColorPickerEventPayload): void {
        this.itemForm.markAsTouched({ onlySelf: true });
        this.borderColor.patchValue(presetName ?? value);
    }

    public setBorderRadius(value: number): void {
        this.itemForm.markAsTouched({ onlySelf: true });
        this.borderRadius.patchValue(value);
    }

    public setInputSize(value: string): void {
        this.itemForm.markAsTouched({ onlySelf: true });
        this.inputSize.patchValue(value as ESize);
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    protected readonly inputBorderEnum = InputBorderEnum;
    protected readonly ERadioGroupType = ERadioGroupType;
}
